import { letraDe } from './franjas-editor.rules';

/**
 * Reglas puras del bloque «Dónde está cada precio» (pantalla 4 del mockup de
 * tramos): Binance P2P en vivo, costo, base, techo/piso de los tramos y la tasa
 * del otro sentido, para VES→USDT y USDT→VES. Sin Angular ni HTTP.
 *
 * Todas las tasas son VES por USDT.
 * - VES→USDT: el cliente PAGA VES. Más alto = más caro para él. Se compara
 *   contra el lado `compra` de Binance (avisos donde el cliente compra USDT).
 * - USDT→VES: el cliente RECIBE VES. Más alto = mejor para él. Se compara
 *   contra el lado `venta` (avisos donde el cliente vende USDT).
 *
 * El bloque es solo referencia: nada de esto alimenta un precio.
 */

export type DireccionPrecio = 'VES-USDT' | 'USDT-VES';

/** Un lado del libro P2P tal como lo manda `GET rates/binance-p2p/usdt-ves`. */
export interface LadoP2p {
  mejor: number;
  promedio: number;
  avisos?: { precio: number; disponible: number; min: number; max: number; anunciante: string }[];
}

/** Respuesta de `GET rates/binance-p2p/usdt-ves`. Si Binance cae, los lados vienen en null. */
export interface PrecioP2pUsdtVes {
  compra: LadoP2p | null;
  venta: LadoP2p | null;
  leidoEn: string | null;
  fuente?: string;
  mensaje?: string;
}

/** Un tramo activo con su tasa (forma de `GET rates/tramos-usdt/{par}`). */
export interface TramoTasa {
  clave?: string;
  orden?: number;
  desde: number;
  hasta: number;
  tasa: number;
}

/** Pasado este tiempo sin una lectura nueva, el precio de Binance se marca viejo. */
export const BINANCE_VIEJO_MS = 5 * 60_000;

const positivo = (x: unknown): number | null => {
  const n = Number(x);
  return x != null && Number.isFinite(n) && n > 0 ? n : null;
};

/** Qué lado de Binance se compara con cada sentido. */
export function ladoDe(direccion: DireccionPrecio): 'compra' | 'venta' {
  return direccion === 'VES-USDT' ? 'compra' : 'venta';
}

/**
 * Normaliza la respuesta de Binance: un lado sin precio utilizable queda en
 * null y, si no queda ninguno, devuelve null (= «Binance no disponible»).
 */
export function normalizarBinance(r: PrecioP2pUsdtVes | null | undefined): PrecioP2pUsdtVes | null {
  if (!r || typeof r !== 'object') return null;
  const limpiar = (l: LadoP2p | null | undefined): LadoP2p | null => {
    const promedio = positivo(l?.promedio);
    const mejor = positivo(l?.mejor);
    if (promedio == null && mejor == null) return null;
    return { ...l, promedio: (promedio ?? mejor) as number, mejor: (mejor ?? promedio) as number };
  };
  const compra = limpiar(r.compra);
  const venta = limpiar(r.venta);
  if (!compra && !venta) return null;
  return { ...r, compra, venta };
}

/** Precio de Binance que se compara con el sentido (el promedio de los avisos grandes). */
export function binanceDe(direccion: DireccionPrecio, p: PrecioP2pUsdtVes | null | undefined): number | null {
  const lado = p ? p[ladoDe(direccion)] : null;
  return positivo(lado?.promedio);
}

/** Tramos con tasa válida, ordenados por `desde`. */
export function tramosValidos(tramos: TramoTasa[] | null | undefined): TramoTasa[] {
  return (tramos || [])
    .filter((t) => positivo(t?.tasa) != null)
    .slice()
    .sort((a, b) => Number(a.desde) - Number(b.desde));
}

/**
 * Techo y piso de lo que cobramos: el extremo de las tasas de los tramos y la
 * base. Sin tramos los dos son la base (todos pagan lo mismo).
 */
export function techoPiso(
  base: number | null | undefined,
  tramos: TramoTasa[] | null | undefined,
): { techo: number | null; piso: number | null } {
  const tasas = tramosValidos(tramos).map((t) => Number(t.tasa));
  const b = positivo(base);
  if (b != null) tasas.push(b);
  if (!tasas.length) return { techo: null, piso: null };
  return { techo: Math.max(...tasas), piso: Math.min(...tasas) };
}

/** Diferencia % de una tasa contra Binance (positiva = número más alto que Binance). */
export function difPct(tasa: number | null | undefined, ref: number | null | undefined): number | null {
  const t = positivo(tasa);
  const r = positivo(ref);
  if (t == null || r == null) return null;
  return (t / r - 1) * 100;
}

/** Margen de Aguacate sobre el costo, en % (en USDT→VES ganamos pagando menos que el costo). */
export function margenSobreCosto(
  direccion: DireccionPrecio,
  tasa: number | null | undefined,
  costo: number | null | undefined,
): number | null {
  const d = difPct(tasa, costo);
  if (d == null) return null;
  return direccion === 'VES-USDT' ? d : -d;
}

/** Diferencias menores a esto se leen «igual que Binance». */
export const EMPATE_PCT = 0.005;

/** Si la tasa le conviene al cliente más que Binance, y el texto de la celda. */
export function lecturaVsBinance(
  direccion: DireccionPrecio,
  pct: number | null,
): { texto: string; ganaBinance: boolean | null } {
  if (pct == null) return { texto: '—', ganaBinance: null };
  if (Math.abs(pct) < EMPATE_PCT) return { texto: 'Igual que Binance', ganaBinance: null };
  const abs = formatoPct(Math.abs(pct));
  if (direccion === 'VES-USDT') {
    return pct > 0
      ? { texto: `+${abs} % más caro`, ganaBinance: false }
      : { texto: `−${abs} % más barato`, ganaBinance: true };
  }
  return pct > 0
    ? { texto: `+${abs} % más VES`, ganaBinance: true }
    : { texto: `−${abs} % menos VES`, ganaBinance: false };
}

export function formatoPct(x: number): string {
  return x.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export interface FilaComparacion {
  etiqueta: string;
  tasa: number;
  vsBinancePct: number | null;
  vsBinanceTexto: string;
  ganaBinance: boolean | null;
  margenPct: number | null;
}

/**
 * Filas de la tabla: una por tramo (A, B, C…) o, sin tramos, una sola con la
 * base.
 */
export function filasComparacion(
  direccion: DireccionPrecio,
  base: number | null | undefined,
  tramos: TramoTasa[] | null | undefined,
  binance: number | null | undefined,
  costo: number | null | undefined,
  etiquetaRango: (t: TramoTasa, i: number, ultimo: boolean) => string = () => '',
): FilaComparacion[] {
  const lista = tramosValidos(tramos);
  const filas = lista.length
    ? lista.map((t, i) => {
        const rango = etiquetaRango(t, i, i === lista.length - 1);
        return { etiqueta: rango ? `${letraDe(i)} · ${rango}` : letraDe(i), tasa: Number(t.tasa) };
      })
    : positivo(base) != null
      ? [{ etiqueta: 'Base (todos)', tasa: Number(base) }]
      : [];
  return filas.map((f) => {
    const vs = difPct(f.tasa, binance);
    const l = lecturaVsBinance(direccion, vs);
    return {
      ...f,
      vsBinancePct: vs,
      vsBinanceTexto: l.texto,
      ganaBinance: l.ganaBinance,
      margenPct: margenSobreCosto(direccion, f.tasa, costo),
    };
  });
}

/** Resumen del encabezado: cuántos tramos le ganan a Binance. */
export function resumenVsBinance(filas: FilaComparacion[], hayBinance: boolean): { texto: string; tono: 'bueno' | 'malo' | 'neutro' } {
  if (!hayBinance) return { texto: 'Binance no disponible', tono: 'neutro' };
  const conDato = filas.filter((f) => f.vsBinancePct != null);
  if (!conDato.length) return { texto: 'Sin tasa para comparar', tono: 'neutro' };
  const ganan = conDato.filter((f) => f.ganaBinance === true);
  if (!ganan.length) return { texto: 'Ninguna tasa le gana a Binance', tono: 'malo' };
  if (ganan.length === conDato.length) {
    return { texto: conDato.length === 1 ? 'Le gana a Binance' : 'Todos los tramos le ganan a Binance', tono: 'bueno' };
  }
  const letras = ganan.map((f) => f.etiqueta.split(' · ')[0]);
  const pct = ganan.length === 1 && ganan[0].vsBinancePct != null
    ? ` (${ganan[0].vsBinancePct < 0 ? '−' : '+'}${formatoPct(Math.abs(ganan[0].vsBinancePct))} %)`
    : '';
  return {
    texto: ganan.length === 1
      ? `Solo el tramo ${letras[0]} le gana a Binance${pct}`
      : `Los tramos ${letras.join(', ')} le ganan a Binance`,
    tono: 'bueno',
  };
}

// ───────────────────────────── Gráfico ─────────────────────────────

export type ClaveLinea = 'techo' | 'piso' | 'binance' | 'costo' | 'contraria';

export interface LineaGrafico {
  clave: ClaveLinea;
  etiqueta: string;
  valor: number;
  /** Posición vertical en % del alto (0 = arriba). */
  topPct: number;
}

export interface PuntoGrafico {
  letra: string;
  etiqueta: string;
  valor: number;
  topPct: number;
  /** Posición horizontal en % del ancho. */
  leftPct: number;
}

export interface ModeloGrafico {
  lineas: LineaGrafico[];
  puntos: PuntoGrafico[];
  /** Banda donde viven los tramos (entre techo y piso). */
  banda: { topPct: number; altoPct: number } | null;
}

/** Aire arriba y abajo para que las líneas extremas no toquen el borde. */
export const AIRE_PCT = 12;

/**
 * Escala lineal: el mayor valor arriba. Con todos los valores iguales se
 * centra (no hay división por cero).
 */
export function escala(valores: (number | null | undefined)[]): (v: number) => number {
  const nums = valores.map(positivo).filter((v): v is number => v != null);
  if (!nums.length) return () => 50;
  const max = Math.max(...nums);
  const min = Math.min(...nums);
  if (max - min < 1e-9) return () => 50;
  const util = 100 - 2 * AIRE_PCT;
  return (v: number) => AIRE_PCT + ((max - v) / (max - min)) * util;
}

export function modeloGrafico(entrada: {
  techo: number | null;
  piso: number | null;
  binance: number | null;
  costo: number | null;
  contraria: number | null;
  tramos: TramoTasa[] | null | undefined;
  etiquetas: Record<ClaveLinea, string>;
  etiquetaRango?: (t: TramoTasa, i: number, ultimo: boolean) => string;
}): ModeloGrafico {
  const lista = tramosValidos(entrada.tramos);
  const y = escala([entrada.techo, entrada.piso, entrada.binance, entrada.costo, entrada.contraria]);
  const lineas: LineaGrafico[] = [];
  const agregar = (clave: ClaveLinea, v: number | null) => {
    const p = positivo(v);
    if (p != null) lineas.push({ clave, etiqueta: entrada.etiquetas[clave], valor: p, topPct: y(p) });
  };
  agregar('techo', entrada.techo);
  // Sin tramos, techo y piso son la misma línea: se pinta una sola.
  if (!(entrada.techo != null && entrada.piso != null && Math.abs(entrada.techo - entrada.piso) < 1e-9)) {
    agregar('piso', entrada.piso);
  }
  agregar('binance', entrada.binance);
  agregar('costo', entrada.costo);
  agregar('contraria', entrada.contraria);
  lineas.sort((a, b) => a.topPct - b.topPct);

  const n = lista.length;
  const puntos = lista.map((t, i) => {
    const rango = entrada.etiquetaRango ? entrada.etiquetaRango(t, i, i === n - 1) : '';
    return {
      letra: letraDe(i),
      etiqueta: rango ? `${letraDe(i)} · ${rango}` : letraDe(i),
      valor: Number(t.tasa),
      topPct: y(Number(t.tasa)),
      leftPct: ((i + 1) / (n + 1)) * 100,
    };
  });

  const techo = positivo(entrada.techo);
  const piso = positivo(entrada.piso);
  const banda = techo != null && piso != null && techo > piso
    ? { topPct: y(techo), altoPct: y(piso) - y(techo) }
    : null;

  return { lineas, puntos, banda };
}

/**
 * Separa las etiquetas de la columna derecha para que no se pisen: recorre
 * de arriba abajo empujando hacia abajo y, si la última se sale del alto, la
 * pila entera sube. Entra y sale en píxeles, en el mismo orden.
 */
export function separarEtiquetas(tops: number[], alto: number, minGap: number): number[] {
  const orden = tops.map((t, i) => ({ t, i })).sort((a, b) => a.t - b.t);
  const out = orden.map((o) => o.t);
  for (let k = 1; k < out.length; k++) {
    if (out[k] - out[k - 1] < minGap) out[k] = out[k - 1] + minGap;
  }
  const limite = alto - minGap;
  if (out.length && out[out.length - 1] > limite) {
    out[out.length - 1] = limite;
    for (let k = out.length - 2; k >= 0; k--) {
      if (out[k + 1] - out[k] < minGap) out[k] = out[k + 1] - minGap;
    }
  }
  for (let k = 0; k < out.length; k++) out[k] = Math.max(0, out[k]);
  const res = new Array<number>(tops.length);
  orden.forEach((o, k) => (res[o.i] = out[k]));
  return res;
}

/** «hace 40 s», «hace 3 min»; null si la fecha no sirve. */
export function hace(iso: string | null | undefined, ahora: number = Date.now()): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const s = Math.max(0, Math.round((ahora - t) / 1000));
  if (s < 60) return `hace ${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `hace ${m} min`;
  return `hace ${Math.round(m / 60)} h`;
}

export function binanceViejo(iso: string | null | undefined, ahora: number = Date.now()): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  return Number.isFinite(t) && ahora - t > BINANCE_VIEJO_MS;
}

// ───────────────────────── Rótulos según el sentido ─────────────────────────

/** Textos del bloque que cambian con el sentido (sin cálculo). */
export interface RotulosSentido {
  /** Subtítulo: unidad y qué significa «más arriba». */
  subtitulo: string;
  /** Rótulo del máximo y del mínimo de la tarjeta «Rango de tramos». */
  maximo: string;
  minimo: string;
  /** Qué tramo da cada extremo. */
  tramoMaximo: string;
  tramoMinimo: string;
  /** Leyenda de las dos líneas del rango y de Binance. */
  leyendaMaximo: string;
  leyendaMinimo: string;
  leyendaBinance: string;
}

/**
 * VES→USDT: el cliente paga VES por USDT y Aguacate COBRA; lo más alto es el
 * tramo chico. USDT→VES: el cliente recibe VES por USDT y Aguacate PAGA; lo
 * más alto es el tramo grande.
 */
export function rotulosSentido(direccion: DireccionPrecio): RotulosSentido {
  return direccion === 'VES-USDT'
    ? {
        subtitulo: 'VES por USDT · VES → USDT: más arriba = más caro para el cliente',
        maximo: 'Cobramos lo máximo',
        minimo: 'Cobramos lo mínimo',
        tramoMaximo: 'tramo chico',
        tramoMinimo: 'tramo grande',
        leyendaMaximo: 'Lo máximo que cobramos (tramo chico)',
        leyendaMinimo: 'Lo mínimo que cobramos (tramo grande)',
        leyendaBinance: 'Binance: lo que pagaría el cliente allá',
      }
    : {
        subtitulo: 'VES por USDT · USDT → VES: más arriba = más VES para el cliente',
        maximo: 'Pagamos lo máximo',
        minimo: 'Pagamos lo mínimo',
        tramoMaximo: 'tramo grande',
        tramoMinimo: 'tramo chico',
        leyendaMaximo: 'Lo máximo que pagamos (tramo grande)',
        leyendaMinimo: 'Lo mínimo que pagamos (tramo chico)',
        leyendaBinance: 'Binance: lo que recibiría el cliente allá',
      };
}
